import React from "react";
import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  CENTER_CHANNELS,
  CenterChannel,
  NotificationEvent,
} from "../../services/notificationCenterService";
import { AUDIENCE_TITLES, blockerText, channelColumn, localize } from "./channelText";

type Props = {
  events: NotificationEvent[];
  /** Omit for a read-only grid (the superadmin view). */
  onToggle?: (event: NotificationEvent, channel: CenterChannel, enabled: boolean) => void;
  /** "EVENT:CHANNEL" keys being saved. */
  saving?: Set<string>;
};

const AUDIENCES: NotificationEvent["audience"][] = ["CLIENT", "OWNER", "ACCOUNT"];

/** One switch: a lock when always sent, a dash when the channel is not used. */
const CellControl: React.FC<{
  event: NotificationEvent;
  channel: CenterChannel;
  onToggle?: Props["onToggle"];
  saving?: Set<string>;
}> = ({ event, channel, onToggle, saving }) => {
  const cell = event.cells[channel];
  if (!cell) {
    return (
      <Tooltip title="Not sent on this channel">
        <Box component="span" sx={{ color: "text.disabled" }}>
          —
        </Box>
      </Tooltip>
    );
  }
  const name = channelColumn(channel, event.audience);
  const title = cell.blocker ? blockerText(cell.blocker, channel) : cell.note ?? (cell.enabled ? "On" : "Off");
  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
        {cell.blocker === "LOCKED" ? (
          <LockOutlinedIcon aria-label="Always sent" sx={{ fontSize: 18, color: "text.secondary" }} />
        ) : (
          <Switch
            size="small"
            checked={cell.enabled}
            disabled={!onToggle || !!cell.blocker || (saving?.has(`${event.key}:${channel}`) ?? false)}
            onChange={(e) => onToggle?.(event, channel, e.target.checked)}
            slotProps={{ input: { "aria-label": `${localize(event.label)} by ${name}` } }}
          />
        )}
        {cell.note && (
          <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
            *
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const MessageText: React.FC<{ event: NotificationEvent }> = ({ event }) => (
  <>
    <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>{localize(event.label)}</Typography>
    <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>{localize(event.description)}</Typography>
  </>
);

/**
 * Which message goes out on which channel, one switch per cell. Grouped by who
 * receives it; a group only shows the channels its messages can use. On a
 * phone each message is stacked with its switches underneath.
 */
const MessagesGrid: React.FC<Props> = ({ events, onToggle, saving }) => {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {AUDIENCES.map((audience) => {
        const rows = events.filter((event) => event.audience === audience);
        if (rows.length === 0) return null;
        const columns = CENTER_CHANNELS.filter((channel) => rows.some((row) => row.cells[channel]));
        const notes = Array.from(
          new Set(
            rows.flatMap((row) =>
              columns.map((channel) => row.cells[channel]?.note).filter((note): note is string => !!note)
            )
          )
        );

        return (
          <Paper
            key={audience}
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}
          >
            {stacked ? (
              <>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.95rem", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
                >
                  {AUDIENCE_TITLES[audience]}
                </Typography>
                {rows.map((event) => (
                  <Box
                    key={event.key}
                    sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
                  >
                    <MessageText event={event} />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.75 }}>
                      {columns
                        .filter((channel) => event.cells[channel])
                        .map((channel) => (
                          <Box key={channel} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                              {channelColumn(channel, audience)}
                            </Typography>
                            <CellControl event={event} channel={channel} onToggle={onToggle} saving={saving} />
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ))}
              </>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", py: 1.5 }}>
                      {AUDIENCE_TITLES[audience]}
                    </TableCell>
                    {columns.map((channel) => (
                      <TableCell
                        key={channel}
                        align="center"
                        sx={{ fontWeight: 600, color: "text.secondary", width: 96, whiteSpace: "nowrap" }}
                      >
                        {channelColumn(channel, audience)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((event) => (
                    <TableRow key={event.key} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 1.25 }}>
                        <MessageText event={event} />
                      </TableCell>
                      {columns.map((channel) => (
                        <TableCell key={channel} align="center">
                          <CellControl event={event} channel={channel} onToggle={onToggle} saving={saving} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {notes.length > 0 && (
              <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
                {notes.map((note) => (
                  <Typography key={note} sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                    * Email: {note.charAt(0).toLowerCase() + note.slice(1)}.
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default MessagesGrid;
